import React, { useEffect, useState } from 'react'
import { useParams, Route, useOutletContext, Routes, useSearchParams } from 'react-router-dom'
import Loading from '@renderer/components/Loading'
import NotFound from '@renderer/pages/NotFound'
import ViewProject from '@renderer/pages/View'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useEnforceProjectLanguage } from '@renderer/hooks'
import FailedToInitialize from '@renderer/pages/FailedToInitialize'
import {
  getStoredProjectEffectiveUpdatedAt,
  setStoredProjectEffectiveUpdatedAt
} from '@renderer/utils/projectVersionCache'

interface User {
  _id: string
  email: string
  username: string
}

const ProjectRoute: React.FC = () => {
  const { projectId } = useParams()
  const [searchParams] = useSearchParams()
  const eventId = searchParams.get('eventId') || undefined
  const [isLoading, setIsLoading] = useState(true)
  const [isValidProject, setIsValidProject] = useState<boolean | null>(null)
  const [isInitialized, setIsInitialized] = useState<boolean | null>(null)
  const { user } = useOutletContext<{ user: User }>()
  const { setConfig } = useConfigStore()
  const { initializeSession, refreshProjectCatalog } = useSessionStateStore()
  // Non-IDR projects force `i18n.language = 'en'` regardless of the
  // operator's persisted preference. Doesn't write to localStorage, so
  // leaving the project (or opening an IDR one next) reverts to
  // whatever the operator had picked globally.
  useEnforceProjectLanguage()

  useEffect(() => {
    const validateAndInitProject = async (): Promise<void> => {
      if (!projectId || !user) {
        setIsValidProject(false)
        setIsLoading(false)
        return
      }

      const configSnapshot = useConfigStore.getState().config

      try {
        const versionResponse = await fetch(
          `${process.env.VITE_API_URL}/api/projects/version?id=${projectId}`,
          {
            credentials: 'include',
            cache: 'no-cache'
          }
        )

        if (!versionResponse.ok) {
          setIsValidProject(false)
          setIsLoading(false)
          return
        }

        const versionPayload = (await versionResponse.json()) as {
          effectiveUpdatedAt: string
        }
        const { effectiveUpdatedAt } = versionPayload

        const storedVersion = getStoredProjectEffectiveUpdatedAt(projectId)
        const configMatches =
          Boolean(
            configSnapshot &&
              '_id' in configSnapshot &&
              configSnapshot._id === projectId
          )
        const versionMatches =
          storedVersion !== undefined && storedVersion === effectiveUpdatedAt

        if (versionMatches && configMatches) {
          setStoredProjectEffectiveUpdatedAt(projectId, effectiveUpdatedAt)
          setIsValidProject(true)
          try {
            await initializeSession({ projectId, user, eventId })
            setIsInitialized(true)
          } catch (error) {
            console.error('Error initializing session:', error)
            setIsInitialized(false)
          } finally {
            setIsLoading(false)
          }
          return
        }

        const projectResponse = await fetch(
          `${process.env.VITE_API_URL}/api/projects?id=${projectId}`,
          {
            credentials: 'include',
            cache: 'no-cache'
          }
        )

        if (!projectResponse.ok) {
          setIsValidProject(false)
          setIsLoading(false)
          return
        }

        const projectData = await projectResponse.json()
        if (!projectData || projectData._id !== projectId) {
          setIsValidProject(false)
          setIsLoading(false)
          return
        }
        setIsValidProject(true)

        const sessionHadSameProjectBefore =
          useSessionStateStore.getState().projectId === projectId

        try {
          setConfig(projectData)
          setStoredProjectEffectiveUpdatedAt(projectId, effectiveUpdatedAt)
          await initializeSession({ projectId, user, eventId })
          if (sessionHadSameProjectBefore) {
            await refreshProjectCatalog(projectId)
          }
          setIsInitialized(true)
        } catch (error) {
          console.error('Error initializing session:', error)
          setIsInitialized(false)
        }
      } catch (error) {
        console.error('Error validating project:', error)
        setIsValidProject(false)
      } finally {
        setIsLoading(false)
      }
    }

    void validateAndInitProject()
    // Intentionally exclude `config` from deps: updates to config from welcome refresh must not re-run bootstrap.
  }, [projectId, user, eventId, initializeSession, refreshProjectCatalog, setConfig])

  if (isLoading) {
    return <Loading />
  }

  if (!isValidProject) {
    return <NotFound />
  }

  if (isInitialized === false) {
    return <FailedToInitialize />
  }

  return (
    <Routes>
      <Route path="/*" element={<ViewProject />} />
    </Routes>
  )
}

export default ProjectRoute
